import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  Tabs,
} from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";

const AGENT_TYPE_LABEL = {
  orchestrator: "Orchestrator",
  sub_agent: "Sub-agent",
  "sub-agent": "Sub-agent",
};

const CATEGORY_OPTIONS = [
  { id: "medical", label: "Medical" },
  { id: "surgical", label: "Surgical" },
  { id: "diagnostic", label: "Diagnostic" },
  { id: "allied", label: "Allied Health" },
  { id: "other", label: "Other" },
];

export default function LibrarySpecialtyDetail() {
  const { specialty } = useParams();
  const nav = useNavigate();
  const { env, isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();
  const [tab, setTab] = useState("overview");

  const [entry, setEntry] = useState(null);
  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "medical",
    icon: "",
    color: "#666666",
    body_systems: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entryRes, agentsRes] = await Promise.all([
        api.getSpecialtyCatalogEntry(specialty),
        api.listAgents({ mode: env }).catch(() => ({ data: [] })),
      ]);
      setEntry(entryRes.data);
      setAllAgents(agentsRes.data || []);
      setForm({
        name: entryRes.data.name || "",
        description: entryRes.data.description || "",
        category: entryRes.data.category || "medical",
        icon: entryRes.data.icon || "",
        color: entryRes.data.color || "#666666",
        body_systems: (entryRes.data.body_systems || []).join(", "),
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [specialty, env]);

  // Agents currently tagged with this specialty (members) vs the rest
  const { members, nonMembers } = useMemo(() => {
    const m = [];
    const n = [];
    for (const a of allAgents) {
      const tags = a.specialties || [];
      if (tags.includes(specialty)) m.push(a);
      else n.push(a);
    }
    m.sort((a, b) => (a.name || a.key).localeCompare(b.name || b.key));
    n.sort((a, b) => (a.name || a.key).localeCompare(b.name || b.key));
    return { members: m, nonMembers: n };
  }, [allAgents, specialty]);

  const addAgentToSpecialty = async (agent) => {
    setBusy(true);
    setError(null);
    try {
      const nextTags = Array.from(
        new Set([...(agent.specialties || []), specialty])
      );
      await api.writeAgent(agent.key, {
        content: agent.content,
        mode: "draft",
        name: agent.name,
        description: agent.description,
        agent_type: agent.agent_type,
        color: agent.color,
        icon: agent.icon,
        enabled: agent.enabled !== false,
        protocol_directory: agent.protocol_directory || null,
        specialties: nextTags,
        tools: agent.tools || [],
        triggers: agent.triggers || [],
        linked_protocols: agent.linked_protocols || [],
        base_version: agent.version,
        author: "admin",
        commit_msg: `Add to specialty ${specialty}`,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const removeAgentFromSpecialty = async (agent) => {
    if (
      !window.confirm(
        `Remove "${agent.name || agent.key}" from specialty "${entry?.name || specialty}"?\n\nThis clears the specialty tag on the agent — it doesn't delete the agent itself.`
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const nextTags = (agent.specialties || []).filter((s) => s !== specialty);
      await api.writeAgent(agent.key, {
        content: agent.content,
        mode: "draft",
        name: agent.name,
        description: agent.description,
        agent_type: agent.agent_type,
        color: agent.color,
        icon: agent.icon,
        enabled: agent.enabled !== false,
        protocol_directory: agent.protocol_directory || null,
        specialties: nextTags,
        tools: agent.tools || [],
        triggers: agent.triggers || [],
        linked_protocols: agent.linked_protocols || [],
        base_version: agent.version,
        author: "admin",
        commit_msg: `Remove from specialty ${specialty}`,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const saveEdit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateSpecialtyCatalogEntry(specialty, {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        icon: form.icon.trim() || form.name.trim().charAt(0).toUpperCase(),
        color: form.color,
        body_systems: form.body_systems
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setEditing(false);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const deleteSpecialty = async () => {
    if (
      !window.confirm(
        `Delete specialty "${entry?.name}"? This only removes the catalog entry. Agent templates and existing sub-tenants are NOT affected.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.deleteSpecialtyCatalogEntry(specialty);
      nav(`${prefix}/specialties`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={entry?.name || specialty}
        subtitle={entry?.description || "Specialty details"}
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Agent Groups", to: `${prefix}/specialties` },
          { label: entry?.name || specialty },
        ]}
        actions={
          <div className="btn-row">
            {!editing && entry && !isLive && (
              <>
                <button className="btn" onClick={() => setEditing(true)} disabled={busy}>
                  Edit
                </button>
                <button
                  className="btn danger"
                  onClick={deleteSpecialty}
                  disabled={busy}
                >
                  Delete
                </button>
              </>
            )}
            {editing && !isLive && (
              <>
                <button
                  className="btn"
                  onClick={() => setEditing(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={saveEdit}
                  disabled={busy}
                >
                  {busy ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        }
      />

      {entry && !editing && (
        <Tabs
          tabs={[
            { id: "overview", label: "Overview" },
            // Orchestration tab removed — chain mode runs the orchestrator in code,
            // not via a prompt-driven agent. See configural-runtime/src/providers/chain.js.
          ]}
          active={tab}
          onChange={setTab}
        />
      )}

      <div className="page-body">
        <ErrorBanner error={error} />

        {loading ? (
          <Loading />
        ) : !entry ? (
          <div className="empty-state">
            <h3>Specialty not found</h3>
            <p>
              <Link to={`${prefix}/specialties`}>Back to specialties</Link>
            </p>
          </div>
        ) : editing ? (
          <EditForm form={form} setForm={setForm} specialty={specialty} />
        ) : (
          <>
            <div className="card">
              <div
                style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 16 }}
              >
                <div
                  className="tile"
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 10,
                    background: entry.color || "#666",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 26,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {entry.icon || entry.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ marginBottom: 4 }}>{entry.name}</h2>
                  <div className="muted small mono">{entry._id}</div>
                  {entry.description && (
                    <p
                      className="muted"
                      style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}
                    >
                      {entry.description}
                    </p>
                  )}
                </div>
              </div>

              <table className="table" style={{ border: "none" }}>
                <tbody>
                  <tr>
                    <th style={{ width: "25%" }}>Category</th>
                    <td>
                      <span className="badge badge-accent">{entry.category}</span>
                    </td>
                  </tr>
                  <tr>
                    <th>Body systems</th>
                    <td>
                      <div className="btn-row" style={{ flexWrap: "wrap" }}>
                        {(entry.body_systems || []).length === 0 ? (
                          <span className="subtle">—</span>
                        ) : (
                          entry.body_systems.map((b) => (
                            <span key={b} className="badge badge-neutral">
                              {b}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Updated</th>
                    <td className="muted small">
                      {entry.updated_at
                        ? new Date(entry.updated_at).toLocaleString()
                        : "—"}{" "}
                      by {entry.updated_by || "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="card" style={{ padding: 0 }}>
              <div
                className="card-header"
                style={{ padding: "16px 20px 12px" }}
              >
                <h3>
                  Agents{" "}
                  <span className="muted small" style={{ fontWeight: 400 }}>
                    ({members.length})
                  </span>
                </h3>
                {!isLive && (
                  <button
                    className="btn sm primary"
                    onClick={() => setShowPicker(true)}
                    disabled={busy}
                  >
                    + Add agents
                  </button>
                )}
              </div>
              <div
                className="muted small"
                style={{ padding: "0 20px 12px" }}
              >
                A specialty is a named grouping of agents with metadata. All
                the logic lives in the agents themselves — add or remove
                agents to change what this specialty delivers.
              </div>

              {members.length === 0 ? (
                <div
                  className="empty-state"
                  style={{ padding: "24px 20px" }}
                >
                  <p>
                    No agents are tagged with this specialty yet.{" "}
                    {!isLive && (
                      <button
                        className="banner-link"
                        onClick={() => setShowPicker(true)}
                      >
                        Add some
                      </button>
                    )}
                  </p>
                </div>
              ) : (
                <table
                  className="table agents-table"
                  style={{ border: "none" }}
                >
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th style={{ width: 110 }}>Type</th>
                      <th className="right" style={{ width: 70 }}>
                        Tools
                      </th>
                      <th className="right" style={{ width: 90 }}>
                        Protocols
                      </th>
                      <th className="right" style={{ width: 80 }}>
                        Triggers
                      </th>
                      {!isLive && <th style={{ width: 90 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((a) => (
                      <tr key={a.key}>
                        <td
                          className="clickable-row"
                          onClick={() =>
                            nav(
                              `${prefix}/agents/${encodeURIComponent(a.key)}`
                            )
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span
                              className="agent-icon-tile"
                              style={{
                                width: 26,
                                height: 26,
                                fontSize: 12,
                                background: a.color || "#455A64",
                                borderRadius: 6,
                              }}
                            >
                              {(a.icon && a.icon.trim()) ||
                                (a.name || a.key).charAt(0).toUpperCase()}
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600 }}>
                                {a.name || a.key}
                              </div>
                              <div className="muted small mono">{a.key}</div>
                            </div>
                          </div>
                        </td>
                        <td className="small">
                          {a.description || (
                            <span className="subtle">no description</span>
                          )}
                        </td>
                        <td className="muted small">
                          {AGENT_TYPE_LABEL[a.agent_type] || a.agent_type || "—"}
                        </td>
                        <td className="right">
                          {(a.tools || []).length > 0 ? (
                            <span className="mono small">
                              {(a.tools || []).length}
                            </span>
                          ) : (
                            <span className="subtle">—</span>
                          )}
                        </td>
                        <td className="right">
                          {(a.linked_protocols || []).length > 0 ? (
                            <span className="mono small">
                              {(a.linked_protocols || []).length}
                            </span>
                          ) : (
                            <span className="subtle">—</span>
                          )}
                        </td>
                        <td className="right">
                          {(a.triggers || []).length > 0 ? (
                            <span className="mono small">
                              {(a.triggers || []).length}
                            </span>
                          ) : (
                            <span className="subtle">—</span>
                          )}
                        </td>
                        {!isLive && (
                          <td className="right">
                            <button
                              className="btn sm"
                              onClick={() => removeAgentFromSpecialty(a)}
                              disabled={busy}
                              title="Remove this agent from the specialty"
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {showPicker && (
          <AddAgentsModal
            nonMembers={nonMembers}
            specialty={specialty}
            specialtyName={entry?.name || specialty}
            onClose={() => setShowPicker(false)}
            onAdd={async (agent) => {
              await addAgentToSpecialty(agent);
            }}
            busy={busy}
          />
        )}
      </div>
    </>
  );
}

function EditForm({ form, setForm, specialty }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Edit specialty</h3>
      </div>

      <div className="form-field">
        <label>Specialty ID</label>
        <input type="text" value={specialty} readOnly style={{ background: "#f6f6f6" }} />
        <div className="help">The ID is immutable. To rename, create a new specialty and delete this one.</div>
      </div>

      <div className="form-field">
        <label>Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </div>

      <div className="form-field">
        <label>Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </div>

      <div className="form-field">
        <label>Category</label>
        <select
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", gap: 16 }}>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Icon (single char)</label>
          <input
            type="text"
            maxLength={2}
            value={form.icon}
            onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
          />
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Color</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              style={{ width: 44, height: 36, padding: 0, border: "1px solid var(--color-border)", borderRadius: 4 }}
            />
            <input
              type="text"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              style={{ flex: 1 }}
            />
          </div>
        </div>
      </div>

      <div className="form-field">
        <label>Body systems (comma separated)</label>
        <input
          type="text"
          value={form.body_systems}
          onChange={(e) => setForm((f) => ({ ...f, body_systems: e.target.value }))}
        />
      </div>
    </div>
  );
}

// ─── AddAgentsModal ──────────────────────────────────────────────────
function AddAgentsModal({
  nonMembers,
  specialty,
  specialtyName,
  onClose,
  onAdd,
  busy,
}) {
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(null); // agent being added

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return nonMembers;
    return nonMembers.filter((a) => {
      const hay = `${a.name || ""} ${a.key} ${a.description || ""} ${(a.specialties || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [nonMembers, search]);

  const handleAdd = async (agent) => {
    setPending(agent.key);
    try {
      await onAdd(agent);
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 820, width: "95%" }}
      >
        <h2>Add agents to {specialtyName}</h2>
        <div className="muted small mb-16">
          Pick one or more agents from the library. Each pick tags the
          agent with <span className="mono">{specialty}</span> and creates a
          new agent version.
        </div>

        <input
          type="text"
          placeholder="Search agents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            fontSize: 13,
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
            marginBottom: 12,
          }}
        />

        <div
          style={{
            maxHeight: "55vh",
            overflowY: "auto",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          {filtered.length === 0 ? (
            <div className="muted small" style={{ padding: 16 }}>
              {nonMembers.length === 0
                ? "Every agent is already tagged with this specialty."
                : "No agents match your search."}
            </div>
          ) : (
            filtered.map((a) => {
              const tags = a.specialties || [];
              const isBusy = pending === a.key;
              return (
                <div
                  key={a.key}
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid var(--color-border)",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span
                    className="agent-icon-tile"
                    style={{
                      width: 28,
                      height: 28,
                      fontSize: 13,
                      background: a.color || "#455A64",
                      borderRadius: 6,
                      flexShrink: 0,
                    }}
                  >
                    {(a.icon && a.icon.trim()) ||
                      (a.name || a.key).charAt(0).toUpperCase()}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{a.name || a.key}</div>
                    <div className="muted small">
                      <span className="mono">{a.key}</span>
                      {a.description && (
                        <>
                          {" · "}
                          {a.description}
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                        marginTop: 4,
                      }}
                    >
                      {tags.length === 0 ? (
                        <span className="subtle small">untagged</span>
                      ) : (
                        tags.map((s) => (
                          <span key={s} className="badge badge-neutral">
                            {s}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => handleAdd(a)}
                    disabled={busy || isBusy}
                  >
                    {isBusy ? "Adding..." : "+ Add"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div
          className="btn-row"
          style={{ marginTop: 16, justifyContent: "flex-end" }}
        >
          <button className="btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

